import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../../database/firebase";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Heart,
  Activity,
  Brain,
  Dumbbell,
  AlertTriangle,
  CheckCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  Shield,
  Zap,
} from "lucide-react";
import {
  calculateBMI,
  getBMICategory,
  calculateOverallHealthScore,
  generateRecommendations,
} from "../../config/healthConfig";

const HealthRiskAssessment = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [riskData, setRiskData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    if (user) {
      performRiskAssessment();
    }
  }, [user]);

  const performRiskAssessment = async () => {
    if (!user?.uid) return;

    setIsLoading(true);
    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();

        // Prepare user data for assessment
        const assessmentData = {
          weight: userData.healthMetrics?.currentWeight || 70,
          height: userData.healthMetrics?.height || 170,
          age: userData.personalDetails?.age || 30,
          gender: userData.personalDetails?.gender || "male",
          activityLevel:
            userData.healthInfo?.physicalFitness?.activityLevel || "moderate",
          healthConditions: userData.healthConditions || {},
          healthInfo: userData.healthInfo || {},
        };

        // Calculate risk assessment
        const assessment = calculateOverallHealthScore(assessmentData);
        const recs = generateRecommendations(assessment, assessmentData);

        setRiskData(assessment);
        setRecommendations(recs);

        // Save assessment to Firestore
        await updateDoc(doc(db, "users", user.uid), {
          "healthInfo.riskAssessment": {
            lastAssessed: new Date().toISOString(),
            overallScore: assessment.overallScore,
            riskLevel: assessment.overallRisk,
            categories: assessment.categories,
            recommendations: recs,
          },
          updatedAt: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error("Error performing risk assessment:", error);
      toast.error("Failed to assess health risks");
    } finally {
      setIsLoading(false);
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case "low":
        return "#00ff88";
      case "moderate":
        return "#ffaa00";
      case "high":
        return "#ff6644";
      case "critical":
        return "#ff4444";
      default:
        return "#00d9ff";
    }
  };

  const getRiskIcon = (risk) => {
    switch (risk) {
      case "low":
        return <CheckCircle className="w-6 h-6" />;
      case "moderate":
        return <AlertTriangle className="w-6 h-6" />;
      case "high":
        return <XCircle className="w-6 h-6" />;
      case "critical":
        return <XCircle className="w-6 h-6" />;
      default:
        return <Shield className="w-6 h-6" />;
    }
  };

  const CategoryCard = ({ title, icon: Icon, data, iconColor }) => {
    const riskColor = getRiskColor(data.risk);

    return (
      <div className="bg-[#1a2f2f] rounded-2xl p-5 border border-[#2a3f3f]">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: iconColor + "20" }}
            >
              <Icon className="w-6 h-6" style={{ color: iconColor }} />
            </div>
            <div>
              <h3 className="text-white font-semibold text-base">{title}</h3>
              <p className="text-gray-400 text-sm capitalize">
                {data.risk} Risk
              </p>
            </div>
          </div>
          <div style={{ color: riskColor }}>{getRiskIcon(data.risk)}</div>
        </div>

        {/* Score Circle */}
        <div className="flex items-center justify-center mb-4">
          <div className="relative w-32 h-32">
            <svg className="transform -rotate-90 w-32 h-32">
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="#0a1f1f"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke={riskColor}
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 56}`}
                strokeDashoffset={`${
                  2 * Math.PI * 56 * (1 - data.score / 100)
                }`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center flex-col">
              <span className="text-3xl font-bold text-white">
                {data.score}
              </span>
              <span className="text-xs text-gray-400">/ 100</span>
            </div>
          </div>
        </div>

        {/* Risk Factors */}
        {data.factors && data.factors.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-400 mb-2">
              Key Factors:
            </h4>
            <div className="flex flex-wrap gap-2">
              {data.factors.map((factor, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-[#0a1f1f] text-xs text-gray-300 rounded-full"
                >
                  {factor.replace(/_/g, " ")}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const RecommendationCard = ({ rec }) => {
    const priorityColors = {
      critical: "#ff4444",
      high: "#ff6644",
      medium: "#ffaa00",
      low: "#00d9ff",
    };

    return (
      <div className="bg-[#1a2f2f] rounded-xl p-4 border border-[#2a3f3f] flex items-start gap-3">
        <div className="text-2xl">{rec.icon}</div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="px-2 py-0.5 rounded-full text-xs font-medium"
              style={{
                backgroundColor: priorityColors[rec.priority] + "20",
                color: priorityColors[rec.priority],
              }}
            >
              {rec.priority} priority
            </span>
          </div>
          <p className="text-white text-sm">{rec.message}</p>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a1f1f] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00d9ff] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Analyzing your health data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1f1f] pb-24">
      <div className="w-full max-w-4xl mx-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0a1f1f] border-b border-[#1a2f2f] px-5 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/tracker")}
              className="p-2 hover:bg-[#1a2f2f] rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-400" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">
                Health Risk Assessment
              </h1>
              <p className="text-sm text-gray-400">
                Last assessed:{" "}
                {riskData ? new Date().toLocaleDateString() : "Never"}
              </p>
            </div>
          </div>
        </div>

        {/* Overall Score */}
        <div className="px-5 py-6">
          <div className="bg-gradient-to-br from-[#1a2f2f] to-[#0a1f1f] rounded-3xl p-6 border border-[#2a3f3f]">
            <div className="text-center mb-4">
              <h2 className="text-gray-400 text-sm mb-2">
                Overall Health Score
              </h2>
              <div className="relative inline-block">
                <svg className="transform -rotate-90 w-40 h-40">
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    stroke="#0a1f1f"
                    strokeWidth="12"
                    fill="none"
                  />
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    stroke={getRiskColor(riskData?.overallRisk)}
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 70}`}
                    strokeDashoffset={`${
                      2 *
                      Math.PI *
                      70 *
                      (1 - (riskData?.overallScore || 0) / 100)
                    }`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center flex-col">
                  <span className="text-5xl font-bold text-white">
                    {riskData?.overallScore || 0}
                  </span>
                  <span className="text-sm text-gray-400">/ 100</span>
                </div>
              </div>
              <div className="mt-4">
                <span
                  className="px-4 py-2 rounded-full text-sm font-semibold"
                  style={{
                    backgroundColor: getRiskColor(riskData?.overallRisk) + "20",
                    color: getRiskColor(riskData?.overallRisk),
                  }}
                >
                  {riskData?.overallRisk?.toUpperCase()} RISK
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Risk Categories */}
        <div className="px-5 py-4">
          <h2 className="text-lg font-semibold text-white mb-4">
            Risk Categories
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {riskData?.categories && (
              <>
                <CategoryCard
                  title="Cardiovascular Health"
                  icon={Heart}
                  data={riskData.categories.cardiovascular}
                  iconColor="#ff6644"
                />
                <CategoryCard
                  title="Metabolic Health"
                  icon={Activity}
                  data={riskData.categories.metabolic}
                  iconColor="#00d9ff"
                />
                <CategoryCard
                  title="Mental Wellness"
                  icon={Brain}
                  data={riskData.categories.mental}
                  iconColor="#ff88ff"
                />
                <CategoryCard
                  title="Physical Fitness"
                  icon={Dumbbell}
                  data={riskData.categories.fitness}
                  iconColor="#00ff88"
                />
              </>
            )}
          </div>
        </div>

        {/* Recommendations */}
        {recommendations && recommendations.length > 0 && (
          <div className="px-5 py-4">
            <h2 className="text-lg font-semibold text-white mb-4">
              Personalized Recommendations
            </h2>
            <div className="space-y-3">
              {recommendations.map((rec, index) => (
                <RecommendationCard key={index} rec={rec} />
              ))}
            </div>
          </div>
        )}

        {/* Refresh Button */}
        <div className="px-5 py-6">
          <button
            onClick={performRiskAssessment}
            disabled={isLoading}
            className="w-full py-3 bg-[#00d9ff] text-[#0a1f1f] rounded-xl font-semibold hover:bg-[#00c0e0] transition-colors flex items-center justify-center gap-2"
          >
            <Zap className="w-5 h-5" />
            Refresh Assessment
          </button>
        </div>
      </div>
    </div>
  );
};

export default HealthRiskAssessment;
